/*eslint  @typescript-eslint/no-use-before-define: 0*/

import _ from "lodash";
import i18n from "@/plugins/i18n";
import { DataOptions } from "vuetify";
import { defineModule } from "direct-vuex";
import { FILTER_NAMES } from "@/static/filterNames";
import { filterCapitalize } from "@/filters/capitalize";
import { moduleActionContext, moduleGetterContext } from "@/store";
import { CriterionBase } from "@/models/api/filters/criterions/CriterionBase";
import {
  automaticSettleAndWriteOffItems,
  automaticSettleItems,
  removePairCostDocumentItemWithDocumentItem,
  getCostDocumentData,
  getDocumentItemDetails,
  getDocumentItemsToPair,
  postToggleCostDocumentItemIsSettled,
  postToggleSaleDocumentItemIsSettled,
  postToggleSaleDocumentWriteOff,
  savePairCostDocumentItemWithDocumentItem,
  pairMultipleDocumentItemsWithCostDocumentItem,
} from "@/requests/costDocument";
import { CostDocumentToPair } from "@/models/api/costDocuments/CostDocumentToPair";
import { DocumentItemToPair } from "@/models/api/costDocuments/DocumentItemsToPair";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import { ConfirmCustomButton, ConfirmModel } from "@/models/components/popup/Confirm";
import { DocumentItemPairDetails } from "@/models/api/costDocuments/DocumentItemPairDetails";
import { CostDocumentDataAfterPair } from "@/models/api/costDocuments/CostDocumentDataAfterPair";

export interface CostDocumentsPairingStateInterface {
  isDocumentItemsToPairLoading: boolean;
  isHideSettled: boolean;
  documentItemsToPair: Array<DocumentItemToPair>;
  itemsForMultiplePair: Array<DocumentItemToPair>;
  isMultiplePairDialog: boolean;
  countDocumentItemsToPair: number;
  documentItemsToPairDataTableOptions: DataOptions;
  documentItemsFilters: null | Array<CriterionBase>;
  costDocumentIdToPair: null | string;
  costDocumentToPair: null | CostDocumentToPair;
  costDocumentItemIdToPair: null | number;
  documentItemDetails: null | DocumentItemPairDetails;
  documentItemDetailsId: null | string;
  selectedDocumentItemId: null | string;
  costDocumentToPairDataTableOptions: DataOptions;
}

export const CostDocumentsPairingState: CostDocumentsPairingStateInterface = {
  isDocumentItemsToPairLoading: false,
  isHideSettled: false,
  documentItemsToPair: [],
  itemsForMultiplePair: [],
  isMultiplePairDialog: false,
  countDocumentItemsToPair: 0,
  documentItemsToPairDataTableOptions: new DefaultDataOptions(),
  documentItemsFilters: null,
  costDocumentIdToPair: null,
  costDocumentToPair: null,
  costDocumentItemIdToPair: null,
  documentItemDetails: null,
  documentItemDetailsId: null,
  selectedDocumentItemId: null,
  costDocumentToPairDataTableOptions: new DefaultDataOptions(),
};

const CostDocumentsPairingModule = defineModule({
  state: CostDocumentsPairingState as CostDocumentsPairingStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setCostDocumentToPairDataTableOptions(state, param: DataOptions) {
      state.costDocumentToPairDataTableOptions = param;
    },
    setDocumentItemsToPairDataTableOptions(state, param: DataOptions) {
      state.documentItemsToPairDataTableOptions = JSON.parse(JSON.stringify(param));
    },
    startDocumentItemsLoading(state) {
      state.isDocumentItemsToPairLoading = true;
    },
    setDocumentItemsToPairFilters(state, param: Array<CriterionBase>) {
      state.documentItemsFilters = param;
    },
    setCostDocumentIdToPair(state, costDocumentId: null | string) {
      state.costDocumentIdToPair = costDocumentId;
    },
    setCostDocumentToPair(state, costDocumentToPair: null | CostDocumentToPair) {
      state.costDocumentToPair = costDocumentToPair;
    },
    setDocumentItemsToPair(state, param: { items: Array<DocumentItemToPair>; count: number }) {
      state.documentItemsToPair = param.items;
      state.countDocumentItemsToPair = param.count;
      state.isDocumentItemsToPairLoading = false;
    },
    setItemsForMultiplePair(state, param: Array<DocumentItemToPair>) {
      state.itemsForMultiplePair = param;
    },
    setIsMultiplePairDialog(state, param: boolean) {
      state.isMultiplePairDialog = param;
    },
    setCostDocumentItemIdToPair(state, id: null | number) {
      state.costDocumentItemIdToPair = id;
      state.documentItemsToPair = [];
    },
    setIsHideSettled(state, param: boolean) {
      state.isHideSettled = param;
    },
    setDocumentItemDetails(state, param: null | DocumentItemPairDetails) {
      state.documentItemDetails = param;
    },
    setSelectedDocumentItemId(state, param: null | string) {
      state.selectedDocumentItemId = param;
    },
    setDocumentItemDetailsId(state, param: null | string) {
      state.documentItemDetailsId = param;
    },
  },
  actions: {
    async openCostDocumentToPair(context, costDocumentId) {
      const { commit, rootCommit } = _moduleActionContext(context);
      commit.setCostDocumentIdToPair(costDocumentId);
      rootCommit.costDocuments.setSelectedCostDocument(costDocumentId);
      const data = await getCostDocumentData(costDocumentId);
      commit.setCostDocumentToPair(data);
      if (data.costDocumentItems.length > 0) {
        commit.setCostDocumentItemIdToPair(data.costDocumentItems[0].id as number);
      }
    },
    async downloadCostDocumentItemsToPair(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.costDocumentItemIdToPair !== null) {
        commit.startDocumentItemsLoading();
        const response = await getDocumentItemsToPair(
          {
            count: state.documentItemsToPairDataTableOptions.itemsPerPage,
            page: state.documentItemsToPairDataTableOptions.page - 1,
            criterions: _.cloneDeep(state.documentItemsFilters),
            filterName: FILTER_NAMES.CostDocumentItemsToPairStandardFilter,
          },
          state.costDocumentItemIdToPair
        );
        commit.setDocumentItemsToPair({
          items: response.items,
          count: response.totalCount,
        });
      }
    },
    async getDocumentItemDetails(context, documentItemId: string) {
      const { commit } = _moduleActionContext(context);
      commit.setDocumentItemDetailsId(documentItemId);
      const resp = await getDocumentItemDetails(documentItemId);
      commit.setDocumentItemDetails(resp);
    },
    async toggleCostDocumentItemIsSettled(context, costDocumentItemId: number) {
      const { rootDispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      await postToggleCostDocumentItemIsSettled(costDocumentItemId);
    },
    async deletePairing(context, documentItemId: string) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      await removePairCostDocumentItemWithDocumentItem(state.costDocumentItemIdToPair as number, documentItemId);
      await dispatch.openCostDocumentToPair(state.costDocumentIdToPair);
      await dispatch.downloadCostDocumentItemsToPair();
      await rootDispatch.costDocuments.getCostDocumentItemsPairForCostDocument();
    },
    async basicPairing(context, documentItemToPair: DocumentItemToPair) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      if (documentItemToPair.pairAmount === 0) {
        const isZeroPairConfirmed = (await rootDispatch.popup.addConfirm(
          new ConfirmModel(
            filterCapitalize(i18n.t("doYouReallyWantToPairWithZeroAmount")),
            filterCapitalize(i18n.t("warning")),
            [
              {
                text: filterCapitalize(i18n.t("cancel")),
                color: "warning",
                value: false,
                outlined: true,
              },
              {
                text: filterCapitalize(i18n.t("confirm")),
                color: "warning",
                value: true,
              },
            ],
            "450px"
          )
        )) as boolean;
        if (!isZeroPairConfirmed) return;
      }
      const response = await savePairCostDocumentItemWithDocumentItem(
        state.costDocumentItemIdToPair as number,
        documentItemToPair.id,
        documentItemToPair.pairAmount as number
      );
      dispatch.handlePairingResponse(response);
    },
    async multipleParing(context, documentItems: Array<DocumentItemToPair>) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      if (documentItems.find((x) => x.pairAmount === 0)) {
        const isZeroPairConfirmed = (await rootDispatch.popup.addConfirm(
          new ConfirmModel(
            filterCapitalize(i18n.t("doYouReallyWantToPairWithZeroAmount")),
            filterCapitalize(i18n.t("warning")),
            [
              {
                text: filterCapitalize(i18n.t("cancel")),
                color: "warning",
                value: false,
                outlined: true,
              },
              {
                text: filterCapitalize(i18n.t("confirm")),
                color: "warning",
                value: true,
              },
            ],
            "450px"
          )
        )) as boolean;
        if (!isZeroPairConfirmed) return;
      }
      const response = await pairMultipleDocumentItemsWithCostDocumentItem(
        state.costDocumentItemIdToPair as number,
        documentItems.map((x) => ({
          documentItemId: x.id,
          pairAmount: x.pairAmount,
        }))
      );
      dispatch.handlePairingResponse(response);
    },
    async handlePairingResponse(context, response: CostDocumentDataAfterPair) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      if (response !== null && (response.documentItemCanSettled || response.costDocumentItemCanSettled)) {
        const buttons = new Array<ConfirmCustomButton>();
        buttons.push({
          text: filterCapitalize(i18n.t("cancel")),
          color: "warning",
          value: null,
          outlined: true,
          insertSpace: true,
        });
        if (response.documentItemCanWriteOff) {
          buttons.push({
            text: filterCapitalize(i18n.t("settleAndWriteOffPairedItems")),
            color: "warning",
            value: true,
          });
        }
        if (response.costDocumentItemCanSettled || response.documentItemCanSettled) {
          buttons.push({
            text: filterCapitalize(i18n.t("settlePairedItems")),
            color: "warning",
            value: false,
          });
        }
        const writeOff = (await rootDispatch.popup.addConfirm(
          new ConfirmModel(
            response.automaticSettledMessage,
            filterCapitalize(i18n.t("automaticSettlement")),
            buttons,
            "1000px"
          )
        )) as boolean | null;
        if (writeOff === true) {
          await dispatch.automaticSettleAndWriteOffItems(response.documentItemId);
        } else if (writeOff === false) {
          await dispatch.automaticSettleItems(response.documentItemId);
        }
      }
      dispatch.downloadCostDocumentItemsToPair();
      dispatch.openCostDocumentToPair(state.costDocumentIdToPair);
      rootDispatch.costDocuments.getCostDocumentItemsPairForCostDocument();
    },
    async automaticSettleAndWriteOffItems(context, documentItemId: string) {
      const { state } = _moduleActionContext(context);
      await automaticSettleAndWriteOffItems(state.costDocumentItemIdToPair as number, documentItemId).catch(() => {
        return;
      });
    },

    async automaticSettleItems(context, documentItemId: string) {
      const { state } = _moduleActionContext(context);
      await automaticSettleItems(state.costDocumentItemIdToPair as number, documentItemId).catch(() => {
        return;
      });
    },
    //TODO - here check method
    async toggleDocumentItemIsSettled(context, documentId: string | null = null) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      let showDialog = false;
      if (documentId == null) {
        documentId = state.documentItemDetailsId;
        showDialog = true;
      }
      const result = await postToggleSaleDocumentItemIsSettled(documentId as string, false, []);
      if (result.showExtendedMessage) {
        const confirm = new ConfirmModel(
          result.message ?? "",
          filterCapitalize(i18n.t("warning")),
          [
            {
              text: "Rozlicz i przypisz",
              color: "warning",
              value: true,
              outlined: true,
            },
            {
              text: "Tylko rozlicz",
              color: "warning",
              value: false,
            },
          ],
          undefined,
          undefined,
          "cost-document-popup-table",
          result
        );
        const confirmResult = (await rootDispatch.popup.addConfirm(confirm)) as {
          selectedItems: number[];
          value: boolean;
        };
        if (confirmResult.value) {
          await postToggleSaleDocumentItemIsSettled(documentId as string, true, confirmResult.selectedItems);
        } else {
          await postToggleSaleDocumentItemIsSettled(documentId as string, true, []);
        }
      }
      if (showDialog) await dispatch.getDocumentItemDetails(documentId as string);
      await rootDispatch.costDocuments.getCostDocumentItemsPairForCostDocument();
    },
    async toggleDocumentItemIsSettledFromInvoiceItemDetails(context, documentItemId: string) {
      const { dispatch, rootDispatch } = _moduleActionContext(context);
      await dispatch.toggleDocumentItemIsSettled(documentItemId);
      await rootDispatch.costDocuments.getCostDocumentItemsPairForCostDocument();
    },
    async toggleDocumentItemWriteOff(context, documentItemId: string) {
      const { rootDispatch } = _moduleActionContext(context);
      await postToggleSaleDocumentWriteOff(documentItemId);
      await rootDispatch.costDocuments.getCostDocumentItemsPairForCostDocument();
    },
  },
});

export default CostDocumentsPairingModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, CostDocumentsPairingModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, CostDocumentsPairingModule);
